import React, { useCallback, useEffect, useState } from 'react';

import {
  Breakpoints,
  CTA,
  Divider,
  Spinner,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
} from '@vw-marketing/us-components';

import {
  ManageDealerCTAContainer,
  ManageDealerDetailContainer,
} from './styles';
import { useManageDealerLayers } from './layer-manager/useManageDealerLayers';
import { useDealer } from '../../app/providers/DealerContext';
import { useHistory } from '../../shared/hooks/use-history';
import { useMyVwContext } from '../../shared/hooks/use-my-vw-context';
import { useTrackingManager } from '../../shared/hooks/use-tracking-manager';
import {
  onVWBasicStepLoadEvent,
  onVWBasicDealerCardButtonClickEvent,
  onVWBasicLayerLoadEvent,
} from '../../shared/tagging/tagging-helpers';
import { useFeatureServices } from '../../shared/hooks/use-feature-services';
import ManageDealerAvailability from './components/manage-dealer-availability';
import ManageDealerInformation from './components/manage-dealer-information';
import PageContainer from '../../shared/components/page-container';
import QuickActions from './components/quick-actions';

export const ManageDealer = () => {
  const [layerActive, setLayerActive] = useState(false);

  useManageDealerLayers({
    dealerLayerActive: layerActive,
    onClose: () => setLayerActive(false),
  });

  const { isLoadingPreferredDealer, userPreferredDealer } = useDealer();
  const { translationHandler } = useMyVwContext();
  const trackingManager = useTrackingManager();
  const history = useHistory();
  const { 'locale-service': localeService } = useFeatureServices();
  const isCanada = localeService?.countryCode === 'CA';

  useEffect(() => {
    // If not loading and no dealer, redirect
    if (!isLoadingPreferredDealer && !userPreferredDealer) {
      history.push('/account-settings');
    }
  }, [isLoadingPreferredDealer, userPreferredDealer, history]);

  // Track dealer details page load when component mounts and dealer data is available
  useEffect(() => {
    if (!isLoadingPreferredDealer && userPreferredDealer) {
      try {
        // Call VWBasic_Step_Load event for dealer details page
        onVWBasicStepLoadEvent(trackingManager, {
          Name: 'Find a Dealer',
          StepName: 'Find a Dealer Map - Dealer Detail',
          StepNumber: '3.1',
        });

        if (isCanada) {
          onVWBasicLayerLoadEvent(trackingManager, 'Find a Volkswagen Dealer');
        }
      } catch (error) {
        console.warn('Error tracking dealer details page load:', error);
      }
    }
  }, [isLoadingPreferredDealer, userPreferredDealer, trackingManager]);

  // Get tracking process data for dealer details page
  const getTrackingProcessData = useCallback(() => {
    return {
      Name: 'Find a Dealer',
      StepName: 'Find a Dealer Map - Dealer Detail',
      StepNumber: '3.1',
    };
  }, []);

  // Track dealer card button clicks
  const trackDealerCardButtonClick = useCallback((linkName: string) => {
    try {
      onVWBasicDealerCardButtonClickEvent(
        trackingManager,
        getTrackingProcessData(),
        { name: linkName }
      );
    } catch (error) {
      console.warn('Error tracking dealer card button click:', error);
    }
  }, [trackingManager, getTrackingProcessData]);

  const handleOpenDealerLayer = () => {
    // Track the "Find a new dealer" button click
    trackDealerCardButtonClick('Find a new dealer');
    
    setLayerActive(true);
  };

  if (isLoadingPreferredDealer) {
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Spinner />
      </div>
    );
  }

  if (!userPreferredDealer) return null;

  return (
    <>
    <PageContainer>
      <ManageDealerDetailContainer>
        <TextCustom
          appearance={TextAppearance.headline400}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize32,
              fontWeight: TextWeight.bold,
              color: TextColor.primary,
              lineHeight: TextLineHeight.TextLineHeight32,
            },
            [Breakpoints.b560]: {
              fontSize: TextSize.TextSize48,
              fontWeight: TextWeight.bold,
              color: TextColor.primary,
              lineHeight: TextLineHeight.TextLineHeight48,
            },
          }}
        >
          <span className="notranslate">{userPreferredDealer.name}</span>
        </TextCustom>

        <Divider />
        <QuickActions />
        <Divider />
        <ManageDealerInformation />
        <Divider />
        <ManageDealerAvailability />
        <Divider />
        <ManageDealerCTAContainer>
          <CTA
            emphasis="secondary"
            onClick={handleOpenDealerLayer}
            stretchContent={true}
            tag="button"
          >
            {translationHandler('dealerManager.details.newDealer')}
          </CTA>
        </ManageDealerCTAContainer>
      </ManageDealerDetailContainer>
    </PageContainer>
  </>
  );
};